import FabricTree from '../dataFabrics/FabricTree';
import HistoryList from './HistoryList';
import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux';
import {
  setSessionId,
  setIsInitialLoading,
  setConversation,
  updateConversation,
  setIsLandPageVisible,
  setSelectedFabricId,
  setSelectedDocIds,
  setProtocolDocId,
  setProtocolMarkdownS3Key,
  setProtocolMarkdownContent,
  setCurrentSidebarView,
  resetCopilotState,
} from '../../../store/copilotSlice';
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { TopNav, SideNav } from "../navigation/ConsoleNavigation";
import ErrorPopup from "../../utils/popup/ErrorPopup";
import ProcessingPopup from "../../utils/popup/ProcessingPopup";
import SuccessPopup from "../../utils/popup/SuccessPopup";
import GetCookie from "../../utils/GetCookie";
import MarkdownComponents from "../../utils/MarkdownComponents"
import GraphComponent from './GraphComponent';
import TableComponent from './TableComponent';
import ImageComponent from './ImageComponent';
import ReferencesComponent from './ReferencesComponent';
import InputSheetTable from './InputSheetTable';
import CopilotThinkingLogs from './CopilotThinkingLogs';
import CopilotMigrateModal from './CopilotMigrateModal';
import DocumentPreview from '../../utils/DocumentPreview';
import axios from 'axios';

const replaceInlineCitations = (text, references, citationMap) => {
  if (!text) return text;
  const docNameMap = {};
  if (citationMap && typeof citationMap === 'object') {
    for (const [id, name] of Object.entries(citationMap)) {
      docNameMap[parseInt(id, 10)] = name;
    }
  }
  if (references && references.length > 0) {
    for (const ref of references) {
      if (ref.doc_id && ref.doc_name) {
        docNameMap[ref.doc_id] = ref.doc_name;
      }
    }
  }
  if (Object.keys(docNameMap).length === 0) return text;
  return text.replace(/\[doc_id_(\d+)\]/g, (match, idStr) => {
    const docId = parseInt(idStr, 10);
    const docName = docNameMap[docId];
    if (!docName) return match;
    return `<span class="ev-message-card6 m-0 mx-1 ev-btn-xsmall ev-bold-reg">${docName}</span>`;
  });
};

const Copilot = () => {
  const location = useLocation();

  const dispatch = useDispatch();

  // ═══ Redux-persisted state (survives page navigation) ═══
  const {
    sessionId,
    conversation,
    isLandPageVisible,
    isInitialLoading,
    selectedFabricId,
    selectedDocIds,
    protocolDocId,
    protocolMarkdownS3Key,
    protocolMarkdownContent,
    currentSidebarView,
  } = useSelector((state) => state.copilot);

  // ═══ Local transient state (resets on mount — intentional) ═══
  const [question, setQuestion] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  const [filterFabricId, setFilterFabricId] = useState(() => {
    const state = location.state;
    return (state?.fromDiscovery && state?.fabricId) ? state.fabricId : null;
  });
  const [selectedThreadId, setSelectedThreadId] = useState(() => {
    const state = location.state;
    return (state?.fromDiscovery && state?.threadId) ? state.threadId : null;
  });

  // Graph selection state
  const [selectedGraphTypes, setSelectedGraphTypes] = useState([]);

  // Capabilities dialog state
  const [isCapabilitiesOpen, setIsCapabilitiesOpen] = useState(false);
  const [selectedTools, setSelectedTools] = useState(['rag', 'image_analysis']);

  // Excel-specific state
  const [isProtocolEditorOpen, setIsProtocolEditorOpen] = useState(false);
  const [isGeneratedModelsOpen, setIsGeneratedModelsOpen] = useState(false);
  const [generatedFiles, setGeneratedFiles] = useState([]);
  const [hasExcelTools, setHasExcelTools] = useState(false);
  const [selectedModelIds, setSelectedModelIds] = useState([]);

  // Protocol editor state
  const [isProtocolLoading, setIsProtocolLoading] = useState(false);
  const [isProtocolSaving, setIsProtocolSaving] = useState(false);
  const [protocolLocked, setProtocolLocked] = useState(false);

  // Migrate modal state
  const [migrateModalOpen, setMigrateModalOpen] = useState(false);
  const [migrateFileS3Path, setMigrateFileS3Path] = useState(null);
  const [migrateFileName, setMigrateFileName] = useState(null);

  // Models refresh state
  const [isModelsRefreshing, setIsModelsRefreshing] = useState(false);

  // Loaded code content keyed by s3_path — { [path]: { loading, content, open } }
  const [loadedCode, setLoadedCode] = useState({});

  const toggleCodeView = async (s3Path, fallbackCode = null) => {
    const entry = loadedCode[s3Path];
    if (entry?.open) {
      setLoadedCode(prev => ({ ...prev, [s3Path]: { ...prev[s3Path], open: false } }));
      return;
    }
    if (entry?.content) {
      setLoadedCode(prev => ({ ...prev, [s3Path]: { ...prev[s3Path], open: true } }));
      return;
    }
    setLoadedCode(prev => ({ ...prev, [s3Path]: { loading: true, content: null, open: true } }));
    try {
      const response = await axios.post('/pceapi/copilot/model-presigned-url', {
        s3_path: s3Path
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `${GetCookie('access_token')}`,
        }
      });
      if (response.data.status === 'success' && response.data.preview_url) {
        const textResp = await fetch(response.data.preview_url);
        if (!textResp.ok) {
          throw new Error(`Failed to fetch file: ${textResp.status}`);
        }
        const text = await textResp.text();
        setLoadedCode(prev => ({ ...prev, [s3Path]: { loading: false, content: text, open: true } }));
      } else {
        if (fallbackCode) {
          setLoadedCode(prev => ({ ...prev, [s3Path]: { loading: false, content: fallbackCode, open: true } }));
        } else {
          console.error('Presigned URL request failed:', response.data);
          setLoadedCode(prev => ({ ...prev, [s3Path]: { loading: false, content: null, open: true } }));
        }
      }
    } catch (err) {
      console.error('Failed to fetch code:', err);
      if (fallbackCode) {
        setLoadedCode(prev => ({ ...prev, [s3Path]: { loading: false, content: fallbackCode, open: true } }));
      } else {
        setLoadedCode(prev => ({ ...prev, [s3Path]: { loading: false, content: null, open: true } }));
      }
    }
  };

  // Excel model preview state
  const [excelPreviewOpen, setExcelPreviewOpen] = useState(false);
  const [excelPreviewS3Path, setExcelPreviewS3Path] = useState(null);
  const [excelPreviewName, setExcelPreviewName] = useState(null);
  const [excelPreviewUrl, setExcelPreviewUrl] = useState(null);


  const handleViewExcelModel = async (s3Path, fileName) => {
    try {
      const response = await axios.post('/pceapi/copilot/model-presigned-url', {
        s3_path: s3Path
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `${GetCookie('access_token')}`,
        }
      });
      if (response.data.status === 'success') {
        setExcelPreviewS3Path(s3Path);
        setExcelPreviewName(fileName);
        setExcelPreviewUrl(response.data.preview_url);
        setExcelPreviewOpen(true);
      }
    } catch (err) {
      console.error('Failed to get preview URL:', err);
    }
  };

  const handleDownloadExcelModel = async (s3Path, fileName) => {
    try {
      const response = await axios.post('/pceapi/copilot/model-presigned-url', {
        s3_path: s3Path
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `${GetCookie('access_token')}`,
        }
      });
      if (response.data.status === 'success') {
        const link = document.createElement('a');
        link.href = response.data.preview_url;
        link.download = fileName || 'download';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error('Failed to get download URL:', err);
    }
  };

  // Derive sibling artifact path from model's s3_path
  // S3 layout: .../{run_ts}/generated_model.xlsx → .../{run_ts}/workbook_builder.py
  const getArtifactPath = (modelS3Path, filename) => {
    if (!modelS3Path) return null;
    const lastSlash = modelS3Path.lastIndexOf('/');
    if (lastSlash === -1) return null;
    return modelS3Path.substring(0, lastSlash + 1) + filename;
  };

  // Input sheet editing state — keyed by message timestamp
  const [editedSheets, setEditedSheets] = useState({});
  const [sheetNlpInputs, setSheetNlpInputs] = useState({});
  const [sheetNlpVisible, setSheetNlpVisible] = useState({});

  // Step detail toggle — tracks which step IDs have details expanded
  const [activeStepDetail, setActiveStepDetail] = useState(null);
  const [expandedStepDetails, setExpandedStepDetails] = useState(new Set());
  // Track which messages have thinking steps expanded/collapsed
  const [expandedThinking, setExpandedThinking] = useState(new Set());
  const [hiddenThinking, setHiddenThinking] = useState(new Set());

  const excelPrompts = [
    {
      id: 'markov',
      title: 'Markov Model',
      description: 'Build a discrete-time Markov chain using country-level economic states (GDP growth, inflation, interest rate regime) with transition matrix estimation and steady-state probabilities.',
      prompt: 'Build a discrete-time Markov chain using country-level economic states (GDP growth, inflation, interest rate regime) with transition matrix estimation and steady-state probabilities.'
    },
    {
      id: 'decision_tree',
      title: 'Decision Tree Model',
      description: 'Build a regression tree using country financial indicators to forecast stock index returns with pruning and cross-validation.',
      prompt: 'Build a regression tree using country financial indicators to forecast stock index returns with pruning and cross-validation.'
    },
    {
      id: 'adapt_model',
      title: 'Adapt Existing Model',
      description: 'Adapt my existing model provided from the country specific parameters present in the parameter csv file.',
      prompt: 'Adapt my existing model provided from the country specific parameters present in the parameter csv file.'
    },
  ];

  const toolGroups = [
    {
      label: 'Document Intelligence',
      tools: [
        { id: 'rag', label: 'Retrieval Augmented Generation (RAG)', toggleable: true, locked: true },
        { id: 'image_analysis', label: 'Deep Image Search', toggleable: true, locked: true },
      ],
    },
    {
      label: 'Data Analysis',
      tools: [
        { id: 'python', label: 'Analysis in Python', toggleable: true },
        { id: 'r', label: 'Analysis in R', toggleable: true },
      ],
    },
    {
      label: 'External Sources',
      tools: [
        { id: 'web_search', label: 'Web Search', toggleable: false },
        { id: 'add_sources', label: '+Add Sources', toggleable: false, style: 'filled' },
      ],
    },
    {
      label: 'Excel Intelligence (Build HE Models & More)',
      badge: 'Excel Pro',
      tools: [
        { id: 'excel_toolkit', label: 'Excel Toolkit', toggleable: true, style: 'green' },
      ],
    },
  ];

  const toggleTool = (tool) => {
    if (!tool.toggleable || tool.locked) return;
    const isSelected = selectedTools.includes(tool.id);
    setSelectedTools(prev => {
      const newTools = isSelected
        ? prev.filter(id => id !== tool.id)
        : [...prev, tool.id];

      setHasExcelTools(newTools.includes('excel_toolkit'));
      return newTools;
    });
  };

  // UI state
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState("");
  const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
  const [popupSuccessMessage, setPopupSuccessMessage] = useState("");

  // Refs
  const messagesEndRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Graph types configuration
  const graphTypes = [
    { id: 'bar', icon: 'bi-bar-chart-fill', label: 'Bar Chart' },
    { id: 'pie', icon: 'bi-pie-chart-fill', label: 'Pie Chart' },
    { id: 'line', icon: 'bi-graph-up', label: 'Line Chart' },
    { id: 'heatmap', icon: 'bi-grid-3x3-gap-fill', label: 'Heatmap' },
    { id: 'histogram', icon: 'bi-reception-3', label: 'Histogram' },
    { id: 'donutt', icon: 'bi-opencollective', label: 'Donut Chart' },
    { id: 'choropleth', icon: 'bi-globe-asia-australia', label: 'Choropleth Geographical Map' },
    { id: 'spider', icon: 'bi-heptagon', label: 'Spider Chart' },
  ];
  const API_BASE_URL = '/pceapi/copilot';

  // Initialize session - only on first mount when no existing session
  useEffect(() => {
    const navState = location.state;
    if (navState?.fromDiscovery) {
      if (navState.fabricId) dispatch(setSelectedFabricId(navState.fabricId));
      if (navState.docId) dispatch(setSelectedDocIds([navState.docId]));
    }
    if (navState?.excelPro) {
      const withExcel = selectedTools.includes('excel_toolkit')
        ? selectedTools
        : [...selectedTools, 'excel_toolkit'];
      setSelectedTools(withExcel);
      setHasExcelTools(true);
    } else {
      setHasExcelTools(selectedTools.includes('excel_toolkit'));
    }
    if (!sessionId) {
      dispatch(setIsInitialLoading(true));
    }
  }, []);

  // Scroll to bottom when conversation updates
  useEffect(() => {
    scrollToBottom();
  }, [conversation]);

  // Handler for when FabricTree selection changes
  const handleFabricSelectionChange = (fabricId, docIds) => {
    dispatch(setSelectedFabricId(fabricId));
    dispatch(setSelectedDocIds(docIds));
  };

  // Handle protocol document change
  const handleProtocolDocChange = async (docId) => {
    if (protocolLocked || isGenerating) return;
    if (!docId) {
      dispatch(setProtocolDocId(null));
      dispatch(setProtocolMarkdownS3Key(null));
      dispatch(setProtocolMarkdownContent(null));
      return;
    }
    dispatch(setProtocolDocId(docId));
    dispatch(setProtocolMarkdownContent(null));
    setIsProtocolLoading(true);
    try {
      const response = await axios.post('/pceapi/copilot/fetch-protocol-markdown', {
        doc_id: docId,
        session_id: sessionId || null,
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `${GetCookie('access_token')}`,
        }
      });
      if (response.data.status === 'success') {
        dispatch(setProtocolMarkdownS3Key(response.data.s3_key));
        dispatch(setProtocolMarkdownContent(response.data.markdown_content));
        setPopupSuccessMessage('Protocol document loaded. Open the Protocol Editor to review and edit.');
        setIsSuccessPopupVisible(true);
      } else {
        dispatch(setProtocolDocId(null));
        setPopupErrorMessage(response.data.message || 'Failed to fetch protocol markdown');
        setIsErrorPopupVisible(true);
      }
    } catch (err) {
      console.error('Failed to fetch protocol markdown:', err);
      dispatch(setProtocolDocId(null));
      setPopupErrorMessage('Failed to fetch protocol markdown');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProtocolLoading(false);
    }
  };

  // Save edited protocol markdown to S3
  const handleSaveProtocol = async (markdownOverride) => {
    const content = markdownOverride || protocolMarkdownContent;
    if (!protocolMarkdownS3Key || !content) return;
    setIsProtocolSaving(true);
    try {
      await axios.post('/pceapi/copilot/save-protocol-markdown', {
        s3_key: protocolMarkdownS3Key,
        markdown_content: content,
        session_id: sessionId || null,
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `${GetCookie('access_token')}`,
        }
      });
      setPopupSuccessMessage('Protocol saved successfully');
      setIsSuccessPopupVisible(true);
    } catch (err) {
      console.error('Failed to save protocol markdown:', err);
      setPopupErrorMessage('Failed to save protocol markdown');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProtocolSaving(false);
    }
  };

  // Auto-clear protocol if its doc is unchecked
  useEffect(() => {
    if (protocolDocId && !selectedDocIds.includes(protocolDocId)) {
      dispatch(setProtocolDocId(null));
      dispatch(setProtocolMarkdownS3Key(null));
      dispatch(setProtocolMarkdownContent(null));
    }
  }, [selectedDocIds, protocolDocId]);

  // Handle graph type selection
  const toggleGraphType = (graphId) => {
    setSelectedGraphTypes(prev =>
      prev.includes(graphId)
        ? prev.filter(id => id !== graphId)
        : [...prev, graphId]
    );
  };

  // Handle sidebar view switching
  const handleSidebarSwitch = (view) => {
    dispatch(setCurrentSidebarView(view));
  };

  // Handle loading history from HistoryList component
  const handleLoadHistory = (historyData, loadedSessionId) => {
    const { messages, last_accessed, fabric_id, generated_models, selected_capabilities,
            protocol_doc_id, protocol_markdown_s3_key } = historyData;

    // Set session context
    dispatch(setSessionId(loadedSessionId));
    dispatch(setSelectedFabricId(fabric_id));
    dispatch(setSelectedDocIds(last_accessed || []));
    dispatch(setIsInitialLoading(false));

    // Restore protocol state
    dispatch(setProtocolDocId(protocol_doc_id || null));
    dispatch(setProtocolMarkdownS3Key(protocol_markdown_s3_key || null));
    dispatch(setProtocolMarkdownContent(null));
    setProtocolLocked(!!protocol_doc_id);

    // Fetch saved protocol content from S3 if key exists
    if (protocol_markdown_s3_key) {
      setIsProtocolLoading(true);
      axios.post('/pceapi/copilot/model-presigned-url', {
        s3_path: protocol_markdown_s3_key
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `${GetCookie('access_token')}`,
        }
      }).then(async (resp) => {
        if (resp.data.status === 'success') {
          const textResp = await fetch(resp.data.preview_url);
          const text = await textResp.text();
          dispatch(setProtocolMarkdownContent(text));
        }
      }).catch((err) => {
        console.error('Failed to load protocol markdown from history:', err);
      }).finally(() => {
        setIsProtocolLoading(false);
      });
    }

    // Populate conversation with loaded messages
    dispatch(setConversation(messages));
    dispatch(setIsLandPageVisible(false));

    // Restore capabilities from session
    if (selected_capabilities && selected_capabilities.length > 0) {
      const capKeys = selected_capabilities.map(c => c.capability_key);
      setSelectedTools(capKeys);
      setHasExcelTools(capKeys.includes('excel_toolkit'));
    } else {
      setSelectedTools(['rag', 'image_analysis']);
      setHasExcelTools(false);
    }

    // Populate generated models from history for the sidebar list
    if (generated_models && generated_models.length > 0) {
      setGeneratedFiles(generated_models.map(m => ({
        id: m.id,
        filename: m.model_name,
        s3_path: m.s3_path,
        message_id: m.message_id,
        generated_at: m.generated_at,
        status: 'ready',
      })));
    } else {
      setGeneratedFiles([]);
    }

    // Switch back to fabric view
    dispatch(setCurrentSidebarView('fabric'));

  };

  // Send a specific text directly (used by inline action buttons)
  const handleSendText = (text, confirmedSheet = null) => {
    if (!text || isGenerating) return;
    setQuestion(text);
    sendQuestion(text, confirmedSheet);
  };

  // Handle sending a question to copilot
  const handleAskQuestion = async () => {
    if (!question || isGenerating) return;
    sendQuestion(question);
  };

  const sendQuestion = async (inputText, confirmedSheet = null) => {
    if (!inputText || isGenerating) return;

    // Check if documents are selected
    if (selectedDocIds.length === 0) {
      setPopupErrorMessage('Please select at least one document to interact with Genie');
      setIsErrorPopupVisible(true);
      return;
    }

    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    // Update UI state
    dispatch(setIsLandPageVisible(false));

    // Prepare the question with graph instructions if any are selected
    let finalQuestion = inputText;
    if (selectedGraphTypes.length > 0) {
      const graphInstructions = selectedGraphTypes.map(type => {
        const graphType = graphTypes.find(g => g.id === type);
        return graphType.label.toLowerCase();
      }).join(', ');
      finalQuestion += `. Generate ${graphInstructions}.`;
    }

    // Store original question for display
    const originalQuestion = inputText;

    // Clear input and graph selections
    setQuestion("");
    setSelectedGraphTypes([]);

    // Add the user's question to the conversation
    dispatch(updateConversation(prev => [...prev, { text: originalQuestion, isUser: true }]));

    // Add initial AI response placeholder
    const aiMessageIndex = Date.now();
    dispatch(updateConversation(prev => [...prev, {
      text: "",
      isUser: false,
      timestamp: aiMessageIndex,
      graphs: [],
      tables: [],
      images: [],
      references: null,
      steps: [],
      generatedModels: [],
      isStreaming: true
    }]));

    setIsGenerating(true);
    if (protocolDocId) setProtocolLocked(true);

    try {
      // Prepare request data - using new backend parameters
      const requestData = {
        question: finalQuestion,
        is_new: isInitialLoading,
        session_id: sessionId || null,
        doc_ids: selectedDocIds,
        fabric_id: selectedFabricId,
        selected_capabilities: selectedTools,
        protocol_doc_id: protocolDocId || null,
        protocol_markdown_s3_key: protocolMarkdownS3Key || null,
      };
      if (confirmedSheet) {
        requestData.confirmed_input_sheet = confirmedSheet;
      }

      // Make the API request with environment-aware URL
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/pceapi/copilot/ask`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': GetCookie('access_token')
        },
        body: JSON.stringify(requestData),
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      // Process the streaming response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();

        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.trim() === '') continue;

          try {
            // Try to parse as JSON (for structured responses)
            const data = JSON.parse(line);

            if (data.session) {
              // Handle new session ID from backend
              dispatch(setSessionId(data.session.session_id));
              dispatch(setIsInitialLoading(false)); // Mark as existing session for subsequent requests
            } else if (data.citation_map) {
              // Store citation map early for inline [doc_id_x] rendering during streaming
              dispatch(updateConversation(prev => prev.map(msg => {
                if (msg.timestamp === aiMessageIndex && !msg.isUser) {
                  return {
                    ...msg,
                    citationMap: data.citation_map
                  };
                }
                return msg;
              })));
            } else if (data.markdown_chunk) {
              // Handle streaming markdown chunks — append word-by-word
              dispatch(updateConversation(prev => prev.map(msg => {
                if (msg.timestamp === aiMessageIndex && !msg.isUser) {
                  return {
                    ...msg,
                    text: (msg.text || '') + data.markdown_chunk
                  };
                }
                return msg;
              })));
            } else if (data.markdown) {
              // Handle complete markdown content from backend (legacy/fallback)
              dispatch(updateConversation(prev => prev.map(msg => {
                if (msg.timestamp === aiMessageIndex && !msg.isUser) {
                  return {
                    ...msg,
                    text: data.markdown
                  };
                }
                return msg;
              })));
            } else if (data.status_update) {
              const updateType = data.status_update.type;

              if (updateType === 'thinking') {
                // Handle thinking tokens — append to the thinking buffer on the current step
                const thinkingContent = data.status_update.content || '';
                dispatch(updateConversation(prev => prev.map(msg => {
                  if (msg.timestamp === aiMessageIndex && !msg.isUser) {
                    const prevSteps = [...(msg.steps || [])];
                    const lastIdx = prevSteps.length - 1;
                    if (lastIdx >= 0) {
                      const lastStep = { ...prevSteps[lastIdx] };
                      lastStep.thinking = (lastStep.thinking || '') + thinkingContent;
                      prevSteps[lastIdx] = lastStep;
                    }
                    return { ...msg, steps: prevSteps };
                  }
                  return msg;
                })));
              } else {
                // Handle status updates — push into per-message steps + accumulate log_timeline
                const statusMsg = data.status_update.message;
                const logLevel = data.status_update.log_level || 'phase';
                if (statusMsg) {
                  dispatch(updateConversation(prev => prev.map(msg => {
                    if (msg.timestamp === aiMessageIndex && !msg.isUser) {
                      const prevSteps = [...(msg.steps || [])];
                      const prevTimeline = [...(msg._logTimeline || [])];
                      prevTimeline.push({ message: statusMsg, log_level: logLevel, log_type: data.status_update.log_type || 'info' });

                      if (logLevel === 'detail') {
                        // Nest detail under the last phase step
                        const lastPhaseIdx = prevSteps.map(s => s.logLevel).lastIndexOf('phase');
                        if (lastPhaseIdx !== -1) {
                          const phaseStep = { ...prevSteps[lastPhaseIdx] };
                          phaseStep.details = [...(phaseStep.details || []), statusMsg];
                          prevSteps[lastPhaseIdx] = phaseStep;
                        }
                        return { ...msg, steps: prevSteps, _logTimeline: prevTimeline };
                      }

                      // Phase-level: mark previous steps completed, add new phase step
                      const completedSteps = prevSteps.map(s => ({ ...s, status: 'completed' }));
                      return {
                        ...msg,
                        _logTimeline: prevTimeline,
                        steps: [...completedSteps, {
                          id: Date.now() + Math.random(),
                          message: statusMsg,
                          status: 'processing',
                          logLevel: logLevel,
                          details: [],
                          detail: data.status_update.detail || null,
                          timestamp: new Date().toISOString()
                        }]
                      };
                    }
                    return msg;
                  })));
                }
              }
            } else if (data.alert) {
              // Handle alert signals
              dispatch(updateConversation(prev => prev.map(msg => {
                if (msg.timestamp === aiMessageIndex && !msg.isUser) {
                  if (data.alert.type === 'graph') {
                    return {
                      ...msg,
                      graphs: [...msg.graphs, {
                        contentId: data.alert.content_id,
                        isLoading: true,
                        data: null
                      }]
                    };
                  } else if (data.alert.type === 'table') {
                    return {
                      ...msg,
                      tables: [...msg.tables, {
                        contentId: data.alert.content_id,
                        isLoading: true,
                        data: null
                      }]
                    };
                  } else if (data.alert.type === 'image') {
                    return {
                      ...msg,
                      images: [...(msg.images || []), {
                        contentId: data.alert.content_id,
                        isLoading: true,
                        data: null
                      }]
                    };
                  }
                }
                return msg;
              })));
            } else if (data.graph) {
              // Handle graph data
              dispatch(updateConversation(prev => prev.map(msg => {
                if (msg.timestamp === aiMessageIndex && !msg.isUser) {
                  return {
                    ...msg,
                    graphs: msg.graphs.map(graph =>
                      graph.contentId === data.graph.graph_id
                        ? { ...graph, isLoading: false, data: data.graph }
                        : graph
                    )
                  };
                }
                return msg;
              })));
            } else if (data.table) {
              // Handle table data
              dispatch(updateConversation(prev => prev.map(msg => {
                if (msg.timestamp === aiMessageIndex && !msg.isUser) {
                  return {
                    ...msg,
                    tables: msg.tables.map(table =>
                      table.contentId === data.table.table_id
                        ? { ...table, isLoading: false, data: data.table }
                        : table
                    )
                  };
                }
                return msg;
              })));
            } else if (data.image) {
              // Handle image data
              dispatch(updateConversation(prev => prev.map(msg => {
                if (msg.timestamp === aiMessageIndex && !msg.isUser) {
                  return {
                    ...msg,
                    images: (msg.images || []).map(img =>
                      img.contentId === data.image.image_id
                        ? { ...img, isLoading: false, data: data.image }
                        : img
                    )
                  };
                }
                return msg;
              })));
            } else if (data.thinking_logs) {
              dispatch(updateConversation(prev => prev.map(msg => {
                if (msg.timestamp === aiMessageIndex && !msg.isUser) {
                  const tl = data.thinking_logs;
                  const localTimeline = msg._logTimeline || [];
                  if (localTimeline.length > 0 && tl && tl.steps) {
                    for (const step of tl.steps) {
                      for (const te of (step.tool_executions || [])) {
                        if (!te.log_timeline || te.log_timeline.length === 0) {
                          te.log_timeline = localTimeline;
                        }
                      }
                    }
                  }
                  return { ...msg, thinkingLogs: tl };
                }
                return msg;
              })));
            } else if (data.references) {
              // Handle references
              dispatch(updateConversation(prev => prev.map(msg => {
                if (msg.timestamp === aiMessageIndex && !msg.isUser) {
                  return {
                    ...msg,
                    references: data.references.content
                  };
                }
                return msg;
              })));
            } else if (data.input_sheet_review) {
              // Handle input sheet review for interactive Excel generation
              dispatch(updateConversation(prev => prev.map(msg => {
                if (msg.timestamp === aiMessageIndex && !msg.isUser) {
                  return {
                    ...msg,
                    inputSheetReview: data.input_sheet_review
                  };
                }
                return msg;
              })));
            } else if (data.excel_file) {
              // Handle Excel file outputs — attach to message AND global list
              const modelEntry = {
                id: data.excel_file.file_id || Date.now(),
                filename: data.excel_file.filename,
                s3_path: data.excel_file.s3_path,
                message_id: data.excel_file.message_id || null,
                generated_at: new Date().toISOString(),
                status: 'ready'
              };
              // Attach to the current AI message
              dispatch(updateConversation(prev => prev.map(msg => {
                if (msg.timestamp === aiMessageIndex && !msg.isUser) {
                  return {
                    ...msg,
                    generatedModels: [...(msg.generatedModels || []), modelEntry]
                  };
                }
                return msg;
              })));
              // Also add to global sidebar list
              setGeneratedFiles(prev => [...prev, modelEntry]);
            } else if (data.error) {
              // Handle errors for both graphs and tables
              if (data.error.content_type === 'graph') {
                dispatch(updateConversation(prev => prev.map(msg => {
                  if (msg.timestamp === aiMessageIndex && !msg.isUser) {
                    return {
                      ...msg,
                      graphs: msg.graphs.map(graph =>
                        graph.contentId === data.error.content_id
                          ? { ...graph, isLoading: false, data: null }
                          : graph
                      )
                    };
                  }
                  return msg;
                })));
              } else if (data.error.content_type === 'table') {
                dispatch(updateConversation(prev => prev.map(msg => {
                  if (msg.timestamp === aiMessageIndex && !msg.isUser) {
                    return {
                      ...msg,
                      tables: msg.tables.map(table =>
                        table.contentId === data.error.content_id
                          ? { ...table, isLoading: false, data: null }
                          : table
                      )
                    };
                  }
                  return msg;
                })));
              } else {
                // System errors or other error types
                throw new Error(data.error.message);
              }
            }
          } catch (parseError) {
            // If not JSON, treat as streaming markdown text
            dispatch(updateConversation(prev => prev.map(msg => {
              if (msg.timestamp === aiMessageIndex && !msg.isUser) {
                return {
                  ...msg,
                  text: msg.text + line + '\n'
                };
              }
              return msg;
            })));
          }
        }
      }

      // Mark streaming as complete and finalize all steps
      dispatch(updateConversation(prev => prev.map(msg => {
        if (msg.timestamp === aiMessageIndex && !msg.isUser) {
          return {
            ...msg,
            isStreaming: false,
            steps: (msg.steps || []).map(s => ({ ...s, status: 'completed' }))
          };
        }
        return msg;
      })));

    } catch (error) {
      console.error("Error:", error);

      if (error.name === 'AbortError') {
        console.log("Request was cancelled");
        dispatch(updateConversation(prev => prev.slice(0, -1)));
      } else {
        const errorMessage = error.message || "Failed to get an answer. Please try again.";
        setIsErrorPopupVisible(true);
        setPopupErrorMessage(errorMessage);

        // Remove the loading message
        dispatch(updateConversation(prev => prev.slice(0, -1)));
      }

    } finally {
      setIsGenerating(false);
    }
  };

  // Handle keypresses (Enter to submit)
  const handleKeyPress = (event) => {
    if (event.key === "Enter" && question && !isGenerating) {
      handleAskQuestion();
    }
  };

  // Start a new conversation → single Redux reset
  const handleNewConversation = () => {
    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Reset all Redux state in one shot
    dispatch(resetCopilotState());

    // Reset local transient state
    setIsGenerating(false);
    setSelectedGraphTypes([]);
    setGeneratedFiles([]);
    setSelectedModelIds([]);
    setSelectedTools(['rag', 'image_analysis']);
    setHasExcelTools(false);
    setProtocolLocked(false);
  };

  // Scroll to bottom of conversation
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Close error popup
  const closeErrorPopup = () => {
    setIsErrorPopupVisible(false);
  };

  return (
    <div className="main-window">
      <TopNav />
      <div className="container-fluid has-inner-panel has-dashboard-header">
        <div className="row">
          <SideNav />
          <main className="col-12 col-md-9 col-lg-10 ms-sm-auto console-main-window">
            {/* Copilot Header — above sidebar */}
            <div className="ev-card-2 dashboard-header">
              {/* Toggle buttons + Capabilities — left */}
              <div className="d-flex align-items-center gap-2">
                <div className="btn-group custom-btn-group-outer">
                  <button
                    className={`btn custom-btn-group-inner ${currentSidebarView === 'fabric' ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'} ev-fs-11`}
                    onClick={() => handleSidebarSwitch('fabric')}
                  >
                    Projects
                  </button>
                  <button
                    className={`btn custom-btn-group-inner ${currentSidebarView === 'history' ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'} ev-fs-11`}
                    onClick={() => handleSidebarSwitch('history')}
                  >
                    My History
                  </button>
                </div>

                {/* Capabilities button + dropdown panel */}
                <div className="capabilities-btn-wrapper">
                  <button
                    className={`btn ev-fs-11 ev-bold-reg capabilities-trigger-btn ${isCapabilitiesOpen ? 'active' : ''}`}
                    onClick={() => setIsCapabilitiesOpen(prev => !prev)}
                  >
                    <i className="bi bi-cpu me-1"></i> Capabilities
                  </button>
                  {isCapabilitiesOpen && (
                    <div className="capabilities-panel">
                      <div className="capabilities-header">
                        <span className="capabilities-title">Select Capabilities</span>
                        <button
                          className="capabilities-close-btn"
                          onClick={() => setIsCapabilitiesOpen(false)}
                        >
                          <i className="bi bi-x"></i>
                        </button>
                      </div>
                      <div className="capabilities-groups">
                        {toolGroups.map((group) => (
                          <div key={group.label} className="capabilities-group">
                            <span className="capabilities-group-label">{group.label} {group.badge && <span className="ev-badge-green-gradient ms-1">{group.badge}</span>}</span>
                            <div className="capabilities-chips">
                              {group.tools.map((tool) => {
                                const isActive = tool.toggleable && selectedTools.includes(tool.id);
                                return (
                                  <button
                                    key={tool.id}
                                    className={`capabilities-chip${tool.style ? ` chip-${tool.style}` : ''}${tool.locked ? ' chip-locked' : ''}${isActive ? ' chip-active' : ''}`}
                                    onClick={() => toggleTool(tool)}
                                  >
                                    {tool.label}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Title — center */}
              <p className="ev-med-p-dark mb-0 ev-bold-reg">
                <i className="bi bi-asterisk"></i> Genie.ai
              </p>

              {/* Right side buttons */}
              <div className="d-flex align-items-center gap-2">
                {/* Generated Models button - only show when Excel tools are selected */}
                {hasExcelTools && (
                  <button
                    className="ev-btn-outline-round ev-bold-reg"
                    onClick={async () => {
                      setIsGeneratedModelsOpen(true);
                      if (sessionId) {
                        try {
                          const resp = await axios.post('/pceapi/copilot/session-models', {
                            session_id: sessionId
                          }, {
                            headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `${GetCookie('access_token')}`,
                            }
                          });
                          if (resp.data.status === 'success' && resp.data.data?.length > 0) {
                            setGeneratedFiles(resp.data.data.map(m => ({
                              id: m.id,
                              filename: m.model_name,
                              s3_path: m.s3_path,
                              message_id: m.message_id,
                              generated_at: m.generated_at,
                              status: 'ready',
                            })));
                          }
                        } catch (err) {
                          console.error('Failed to fetch session models:', err);
                        }
                      }
                    }}
                  >
                    <i className="bi bi-collection"></i> Generated Models
                  </button>
                )}

                {/* Model Protocol Editor button - only show when Excel tools are selected */}
                {hasExcelTools && (
                  <button
                    className="ev-btn-outline-round ev-bold-reg ev-btn-green-outline"
                    onClick={() => setIsProtocolEditorOpen(true)}
                  >
                    <i className="bi bi-pencil"></i> Model Protocol Editor
                  </button>
                )}

                {/* Start New Chat — right */}
                <button
                  className="ev-btn-gradient-fill-round ev-bold-reg"
                  onClick={handleNewConversation}
                >
                  <i className="bi bi-plus-circle"></i> Start New Chat
                </button>
              </div>
            </div>

            {/* Conditional Sidebar Rendering */}
            {currentSidebarView === 'fabric' ? (
              <FabricTree
                onSelectionChange={handleFabricSelectionChange}
                apiBaseUrl={API_BASE_URL}
                initialFabricId={selectedFabricId}
                initialSelectedDocIds={selectedDocIds}
                initialFabricName={null}
                autoExpand={!!selectedFabricId && selectedDocIds.length > 0}
                filterFabricId={filterFabricId}
                initialThreadId={selectedThreadId}
                hasExcelTools={hasExcelTools}
                protocolDocId={protocolDocId}
                onProtocolDocChange={handleProtocolDocChange}
              />
            ) : (
              <HistoryList
                onLoadHistory={handleLoadHistory}
              />
            )}


            <div className="inner-window1 inner-window-copilot-padding">
              {isLandPageVisible && (
                <div>
                  <p className="ev-xlarge-p-dark-blue mb-0 mt-2"> Hello</p>
                  <p className="ev-xlarge-p-gradient mb-0">How can I help you today?</p>
                  <br />

                  <div className="row">
                    <div className="col-4 d-flex">
                      <div className="ev-card-9 w-100 " >
                        <div className=" text-end margin-bottom-5">
                          <div className="ev-message-card3 ev-fs-15 ev-radius-large ev-icon-btn-padding-small">
                            <i className="bi bi-lightning-fill"></i>
                          </div>
                        </div>
                        <p className="ev-reg-p-dark mb-1 ev-bold-med">
                          Lightning Fast
                        </p>
                        <p className="mb-0 ev-small-p-blue">
                          Genie sifts through your selected documents,
                          illuminating key findings like a beacon in a sea of
                          data. Make informed decisions faster than ever before.
                        </p>
                      </div>
                    </div>
                    <div className="col-4 d-flex">
                      <div className="ev-card-9 w-100" >
                        <div className="text-end margin-bottom-5">
                          <div className="ev-message-card3 ev-fs-15 ev-radius-large ev-icon-btn-padding-small">
                            <i className="bi bi-intersect"></i>
                          </div>
                        </div>
                        <p className="ev-reg-p-dark mb-1 ev-bold-med" >
                          Cross-Data Detective
                        </p>
                        <p className="mb-0 ev-small-p-blue">
                          Connect the dots across your data, revealing hidden connections and findings
                          you never knew existed. Genie analyzes relationships between data seamlessly.
                        </p>
                      </div>
                    </div>
                    <div className="col-4 d-flex">
                      <div className="ev-card-9 w-100"  >
                        <div className="text-end margin-bottom-5">
                          <div className="ev-message-card3 ev-fs-15 ev-radius-large ev-icon-btn-padding-small">
                            <i className="bi bi-chat-fill"></i>
                          </div>
                        </div>
                        <p className="ev-reg-p-dark mb-1 ev-bold-med">
                          Examples
                        </p>
                        <ul className="ev-small-p-blue">
                          <li className="mb-0">
                            Give me highlights on disease burden?
                          </li>
                          <li className="mb-0">
                            What evidence supports the efficacy claims?
                          </li>
                          <li className="mb-0">
                            Give me table of treatment options available?
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Excel-specific prompts - below default cards */}
                  {hasExcelTools && (
                    <>
                      <p className="text-center ev-med-p-dark ev-bold-med mt-3 mb-2">Examples</p>
                      <div className="row justify-content-center">
                        {excelPrompts.map((prompt) => (
                          <div key={prompt.id} className="col-lg-4 col-md-6 col-12 d-flex mb-2">
                            <div className="ev-card-excel-prompt ev-card-excel-prompt-sm w-100" onClick={() => {
                              setQuestion(prompt.prompt);
                            }}>
                              <div className="d-flex align-items-start justify-content-between">
                                <p className="ev-reg-p-dark mb-1 ev-bold-med ev-fs-13">
                                  {prompt.title}
                                </p>
                                <button className="ev-btn-copy-prompt ev-btn-copy-prompt-sm">
                                  Copy Prompt
                                </button>
                              </div>
                              <p className="mb-0 ev-small-p-blue ev-fs-12">
                                {prompt.description}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="chat-window pb-5">
                {conversation.map((message, index) => (
                  <div
                    key={index}
                    className={`chat-message ${message.isUser ? "user" : "ai"}`}
                  >
                    <p>
                      <b>
                        {message.isUser ? (
                          <>
                            <i className="bi bi-person-fill chat-icon"></i> You.{" "}
                          </>
                        ) : (
                          <>
                            <i className="bi bi-asterisk gradient-font5 chat-icon"></i>{" "}
                            Genie.{" "}
                            {((message.steps && message.steps.length > 0) || (message.thinkingLogs && message.thinkingLogs.steps && message.thinkingLogs.steps.length > 0)) && (() => {
                              const isStillThinking = message.isStreaming;
                              const isHidden = hiddenThinking.has(message.timestamp);
                              const isExplicitlyExpanded = expandedThinking.has(message.timestamp);
                              const isVisible = isStillThinking ? !isHidden : isExplicitlyExpanded;

                              if (isStillThinking) {
                                return (
                                  <button
                                    className="copilot-thinking-toggle"
                                    onClick={() => {
                                      setHiddenThinking(prev => {
                                        const next = new Set(prev);
                                        if (next.has(message.timestamp)) next.delete(message.timestamp);
                                        else next.add(message.timestamp);
                                        return next;
                                      });
                                    }}
                                  >
                                    {isVisible ? 'Hide Thinking' : 'Show Thinking'}
                                    <i className={`bi bi-chevron-${isVisible ? 'up' : 'right'}`}></i>
                                  </button>
                                );
                              }

                              const first = message.steps[0]?.timestamp;
                              const last = message.steps[message.steps.length - 1]?.timestamp;
                              let duration = '';
                              if (first && last) {
                                const seconds = Math.max(1, Math.round((new Date(last) - new Date(first)) / 1000));
                                if (seconds < 60) duration = `${seconds} second${seconds !== 1 ? 's' : ''}`;
                                else if (seconds < 3600) { const m = Math.floor(seconds / 60); duration = `${m} minute${m !== 1 ? 's' : ''}`; }
                                else { const h = Math.floor(seconds / 3600); const rm = Math.floor((seconds % 3600) / 60); duration = `${h}h ${rm}m`; }
                              }

                              return (
                                <button
                                  className="copilot-thinking-toggle"
                                  onClick={() => {
                                    setExpandedThinking(prev => {
                                      const next = new Set(prev);
                                      if (next.has(message.timestamp)) next.delete(message.timestamp);
                                      else next.add(message.timestamp);
                                      return next;
                                    });
                                  }}
                                >
                                  Thought{duration ? ` for ${duration}` : ''}
                                  <i className={`bi bi-chevron-${isExplicitlyExpanded ? 'down' : 'right'}`}></i>
                                </button>
                              );
                            })()}
                          </>
                        )}
                      </b>
                      {!message.isUser && message.text === "" && message.isStreaming && (!message.steps || message.steps.length === 0) ? (
                        <span className="gradient-font5 ev-bold-reg">
                          Thinking{" "}
                          <img
                            src="media/loader2.svg"
                            className="w-8"
                            alt="Loading"
                          />
                        </span>
                      ) : (
                        message.isUser ? (
                          message.text
                        ) : (
                          <div>
                            {/* Execution Logs */}
                            {((message.steps && message.steps.length > 0) || (message.thinkingLogs && message.thinkingLogs.steps && message.thinkingLogs.steps.length > 0)) && (() => {
                              const isStillThinking = message.isStreaming;
                              const isHidden = hiddenThinking.has(message.timestamp);
                              const isExplicitlyExpanded = expandedThinking.has(message.timestamp);
                              const showSteps = isStillThinking ? !isHidden : isExplicitlyExpanded;

                              return (
                                <div className="copilot-thinking-wrapper">
                                  {showSteps ? (
                                    message.thinkingLogs ? (
                                      <div className="scrollable-element copilot-steps-scroll-area">
                                        <CopilotThinkingLogs thinkingLogs={message.thinkingLogs} />
                                        {isStillThinking && (
                                          <div className="copilot-step-bar copilot-step-active mt-2">
                                            <div className="copilot-step-left">
                                              <span className="copilot-step-loader">
                                                <img src="media/loader2.svg" className="copilot-step-loader-img" alt="" />
                                              </span>
                                              <span className="copilot-step-label gradient-font5">Generating final answer...</span>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    ) : !message.isStreaming ? (
                                    /* ── Completed: chain accordion form ── */
                                    <div className="scrollable-element copilot-steps-scroll-area">
                                      <div className="copilot-steps-container">
                                        {message.steps.map((step, idx) => (
                                          <div key={step.id} className="mb-2">
                                            <button
                                              type="button"
                                              className="w-100 d-flex align-items-center justify-content-between ev-message-card2 border-0 text-start"
                                              style={{
                                                borderRadius: expandedStepDetails.has(step.id)
                                                  ? 'var(--px-10) var(--px-10) 0 0'
                                                  : 'var(--px-10)',
                                                padding: 'var(--px-10) var(--px-15)'
                                              }}
                                              onClick={() => step.details?.length > 0 && setExpandedStepDetails(prev => {
                                                const next = new Set(prev);
                                                if (next.has(step.id)) next.delete(step.id);
                                                else next.add(step.id);
                                                return next;
                                              })}
                                            >
                                              <div className="d-flex align-items-center gap-2 mw-0">
                                                <i className="bi bi-check-circle-fill copilot-step-check"></i>
                                                <span className="ev-bold-med ev-xsmall-p-dark text-nowrap">Step {idx + 1}</span>
                                                <span className="ev-xsmall-p-dark">{step.message}</span>
                                              </div>
                                              {step.details && step.details.length > 0 && (
                                                <div className="d-flex align-items-center gap-1 ms-2 flex-shrink-0">
                                                  <span className="ev-xsmall-p-dark">{step.details.length} detail{step.details.length !== 1 ? 's' : ''}</span>
                                                  <i className={`bi bi-chevron-${expandedStepDetails.has(step.id) ? 'up' : 'down'} ev-small-p-blue`}></i>
                                                </div>
                                              )}
                                            </button>
                                            {expandedStepDetails.has(step.id) && step.details && step.details.length > 0 && (
                                              <div className="ev-card-2 ev-padding-small ev-radius-xsmall" style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
                                                {step.details.map((detail, di) => (
                                                  <div key={di} className="d-flex align-items-start gap-2 mb-1">
                                                    <i className="bi bi-chevron-right ev-xsmall-p-dark" style={{ fontSize: 'var(--px-9)', marginTop: '3px', flexShrink: 0 }}></i>
                                                    <span className="ev-xsmall-p-dark">{detail}</span>
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                    ) : (
                                    /* ── Streaming: flat bars with "Show more" for details ── */
                                    <div
                                      className="scrollable-element copilot-steps-scroll-area"
                                      ref={(el) => { if (el) el.scrollTop = el.scrollHeight; }}
                                    >
                                      <div className="copilot-steps-container">
                                        {message.steps.map((step) => (
                                          <div key={step.id}>
                                            <div
                                              className={`copilot-step-bar ${step.status === 'processing' ? 'copilot-step-active' : 'copilot-step-done'}`}
                                            >
                                              <div className="copilot-step-left">
                                                {step.status === 'processing' ? (
                                                  <span className="copilot-step-loader">
                                                    <img src="media/loader2.svg" className="copilot-step-loader-img" alt="" />
                                                  </span>
                                                ) : (
                                                  <i className="bi bi-check-circle-fill copilot-step-check"></i>
                                                )}
                                                <span className="copilot-step-label">{step.message}</span>
                                              </div>
                                              {step.details && step.details.length > 0 && (
                                                <button
                                                  className="ev-btn-light-fill ev-btn-xsmall ev-radius-xsmall border-0 flex-shrink-0"
                                                  style={{ fontSize: 'var(--px-11)', cursor: 'pointer' }}
                                                  onClick={() => setExpandedStepDetails(prev => {
                                                    const next = new Set(prev);
                                                    if (next.has(step.id)) next.delete(step.id);
                                                    else next.add(step.id);
                                                    return next;
                                                  })}
                                                >
                                                  {expandedStepDetails.has(step.id) ? 'Less' : 'More'}{' '}
                                                  <i className={`bi bi-chevron-${expandedStepDetails.has(step.id) ? 'up' : 'down'}`}></i>
                                                </button>
                                              )}
                                            </div>
                                            {expandedStepDetails.has(step.id) && step.details && step.details.length > 0 && (
                                              <div className="ps-3 pb-2">
                                                {step.details.map((detail, di) => (
                                                  <div key={di} className="d-flex align-items-start gap-1">
                                                    <i className="bi bi-chevron-right ev-xsmall-p-dark" style={{ fontSize: 'var(--px-9)', marginTop: '3px' }}></i>
                                                    <span className="ev-xsmall-p-dark">{detail}</span>
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        ))}
                                        {message.isStreaming && message.text === "" && message.steps.every(s => s.status === 'completed') && (
                                          <div className="copilot-step-bar copilot-step-active">
                                            <div className="copilot-step-left">
                                              <span className="copilot-step-loader">
                                                <img src="media/loader2.svg" className="copilot-step-loader-img" alt="" />
                                              </span>
                                              <span className="copilot-step-label gradient-font5">Generating final answer...</span>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    )
                                  ) : (
                                    isStillThinking && (
                                      <span className="gradient-font5 ev-bold-reg">
                                        Thinking{" "}
                                        <img
                                          src="media/loader2.svg"
                                          className="w-8"
                                          alt=""
                                        />
                                      </span>
                                    )
                                  )}
                                </div>
                              );
                            })()}

                            {/* Markdown Content */}
                            {message.text && (
                              <div className="markdown-justified">
                                <ReactMarkdown
                                  remarkPlugins={[remarkGfm]}
                                  rehypePlugins={[rehypeRaw]}
                                  components={MarkdownComponents}
                                >
                                  {replaceInlineCitations(message.text, message.references, message.citationMap)}
                                </ReactMarkdown>
                              </div>
                            )}

                            {/* Generated Excel Models — same style as References */}
                            {message.generatedModels && message.generatedModels.length > 0 && (
                              <div className="ev-card-8 ev-radius-small w-100 mb-2">
                                <h6 className="ev-reg-p-blue">
                                  <i className="bi bi-file-earmark-excel"></i> {message.generatedModels.some(f => (f.filename || f.model_name || '').startsWith('adapted_')) ? 'Adapted Model' : 'Generated Models'}
                                </h6>
                                {message.generatedModels.map((file, index) => {
                                  const name = file.filename || file.model_name;
                                  const isAdaptedModel = name && name.startsWith('adapted_');
                                  const codePath = isAdaptedModel ? null : getArtifactPath(file.s3_path, 'workbook_builder.py');
                                  const inputSheetPath = isAdaptedModel ? null : getArtifactPath(file.s3_path, 'input_sheet.xlsx');
                                  const fallbackCode = (() => {
                                    if (!message.thinkingLogs?.steps) return null;
                                    for (const step of message.thinkingLogs.steps) {
                                      for (const te of (step.tool_executions || [])) {
                                        if (te.generated_code) return te.generated_code;
                                      }
                                    }
                                    return null;
                                  })();
                                  return (
                                    <div key={file.id || index} className="mb-3">
                                      {/* Model row — original UI */}
                                      <div className="d-flex align-items-center justify-content-between mb-2">
                                        <span
                                          className="badge bg-primary ev-fs-11 avatar-tooltip ev-btn-small pe-1 me-1"
                                          data-tooltip={name}
                                        >
                                          <span>
                                            {name.length > 15 ? name.substring(0, 15) + '...' : name}
                                            &nbsp;
                                          </span>
                                          {file.s3_path && (
                                            <button
                                              onClick={() => handleViewExcelModel(file.s3_path, name)}
                                              className="ms-2 ev-btn-white-fill-round ev-btn-xsmall"
                                            >
                                              View <i className="bi bi-eye"></i>
                                            </button>
                                          )}
                                        </span>
                                        {file.s3_path && (
                                          <div className="d-flex gap-2">
                                            <button className="ev-btn-blue-fill-round ev-btn-small ms-2" onClick={() => handleDownloadExcelModel(file.s3_path, name)}>
                                              <i className="bi bi-download"></i> Download
                                            </button>
                                            <button
                                              className="ev-btn-small ev-btn-outline"
                                              onClick={() => {
                                                setMigrateFileS3Path(file.s3_path);
                                                setMigrateFileName(name);
                                                setMigrateModalOpen(true);
                                              }}
                                            >
                                              <i className="bi bi-arrow-left-right"></i> Migrate
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                      {/* Input Sheet row */}
                                      {inputSheetPath && (
                                        <div className="d-flex align-items-center justify-content-between ev-message-card2 p-2 ev-radius-xsmall mb-2">
                                          <div className="d-flex align-items-center gap-2">
                                            <i className="bi bi-table ev-small-p-blue"></i>
                                            <span className="ev-bold-med ev-small-p-dark">input_sheet.xlsx</span>
                                          </div>
                                          <div className="d-flex gap-1">
                                            <button className="ev-btn-small ev-btn-outline" onClick={() => handleViewExcelModel(inputSheetPath, 'input_sheet.xlsx')}>
                                              <i className="bi bi-eye"></i> View
                                            </button>
                                            <button className="ev-btn-blue-fill-round ev-btn-small" onClick={() => handleDownloadExcelModel(inputSheetPath, 'input_sheet.xlsx')}>
                                              <i className="bi bi-download"></i> Download
                                            </button>
                                          </div>
                                        </div>
                                      )}
                                      {/* Workbook Builder Code row */}
                                      {codePath && (
                                        <div className="ev-message-card2 p-2 ev-radius-xsmall w-100">
                                          <div className="d-flex align-items-center justify-content-between">
                                            <div className="d-flex align-items-center gap-2">
                                              <i className="bi bi-code-slash ev-small-p-blue"></i>
                                              <span className="ev-bold-med ev-small-p-dark">workbook_builder.py</span>
                                            </div>
                                            <div className="d-flex gap-1">
                                              <button className="ev-btn-small ev-btn-outline" onClick={() => toggleCodeView(codePath, fallbackCode)}>
                                                <i className={`bi ${loadedCode[codePath]?.open ? 'bi-chevron-up' : 'bi-eye'}`}></i> {loadedCode[codePath]?.open ? 'Collapse' : 'View'}
                                              </button>
                                              {/* {loadedCode[codePath]?.content && (
                                                <button className="ev-btn-small ev-btn-outline" onClick={() => { navigator.clipboard.writeText(loadedCode[codePath].content); }}>
                                                  <i className="bi bi-copy"></i> Copy
                                                </button>
                                              )} */}
                                              <button className="ev-btn-blue-fill-round ev-btn-small" onClick={() => handleDownloadExcelModel(codePath, 'workbook_builder.py')}>
                                                <i className="bi bi-download"></i> Download
                                              </button>
                                            </div>
                                          </div>
                                          {loadedCode[codePath]?.open && (
                                            <div className="mt-2 w-100">
                                              {loadedCode[codePath]?.loading ? (
                                                <div className="text-center p-3">
                                                  <div className="spinner-border spinner-border-sm ev-med-p-blue" role="status"></div>
                                                </div>
                                              ) : loadedCode[codePath]?.content ? (
                                                <pre className="ev-card-2 ev-radius-xsmall ev-small-p-dark scrollable-element vh-30 p-2 mb-0 w-100" style={{ fontSize: 'var(--px-11)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace' }}>
                                                  {loadedCode[codePath].content}
                                                </pre>
                                              ) : (
                                                <p className="ev-small-p-dark mb-0">Unable to load code.</p>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {/* Graphs - contentId as key for 1 to N graphs */}
                            {message.graphs && message.graphs.map((graph) => (
                              <GraphComponent
                                key={graph.contentId}
                                graphData={graph.data}
                                isLoading={graph.isLoading}
                                contentId={graph.contentId}
                              />
                            ))}

                            {/* Tables - contentId as key for 1 to N tables */}
                            {message.tables && message.tables.map((table) => (
                              <TableComponent
                                key={table.contentId}
                                tableData={table.data}
                                isLoading={table.isLoading}
                                contentId={table.contentId}
                              />
                            ))}

                            {/* Images - contentId as key for 1 to N images */}
                            {message.images && message.images.length > 0 && message.images.map((img, imgIdx) => (
                              <ImageComponent
                                key={img.contentId}
                                imageData={img.data}
                                isLoading={img.isLoading}
                                contentId={imgIdx + 1}
                                subtitle={img.data?.subtitle || ""}
                                footer={img.data?.footer || null}
                              />
                            ))}

                            {/* References — rendered here only when no input sheet (otherwise shown below the sheet) */}
                            {message.references && !message.inputSheetReview && (
                              <ReferencesComponent references={message.references} />
                            )}

                            {/* Generate Model button after diagram images — only if a pending input sheet exists */}
                            {message.images && message.images.length > 0 && !message.isStreaming && !message.inputSheetReview && (() => {
                              const pendingSheet = [...conversation].reverse().find(m => !m.isUser && m.inputSheetReview);
                              if (!pendingSheet) return null;
                              const sheet = editedSheets[pendingSheet.timestamp] || pendingSheet.inputSheetReview;
                              const imageS3Paths = message.images
                                .filter(img => img.data?.s3_path)
                                .map(img => img.data.s3_path);
                              return (
                                <div className="input-sheet-actions mt-3">
                                  <div className="ev-bold-med ev-small-p-blue mb-2">
                                    <i className="bi bi-asterisk gradient-font5 me-1"></i> What would you like to do next?
                                  </div>
                                  <div className="input-sheet-actions-row">
                                    <button
                                      className="ev-btn-gradient-fill-round ev-bold-reg"
                                      disabled={isGenerating}
                                      onClick={() => {
                                        const sheetWithDiagram = {
                                          ...sheet,
                                          model_diagram_s3_paths: imageS3Paths,
                                        };
                                        handleSendText("Proceed with generation", sheetWithDiagram);
                                      }}
                                    >
                                      <i className="bi bi-play-circle"></i> Generate Model
                                    </button>
                                  </div>
                                </div>
                              );
                            })()}

                            {/* Input Sheet Editable Table + Actions */}
                            {message.inputSheetReview && !message.isStreaming && message.text && (
                              <>
                                <InputSheetTable
                                  inputSheet={editedSheets[message.timestamp] || message.inputSheetReview}
                                  onSheetChange={(updated) => setEditedSheets(prev => ({
                                    ...prev,
                                    [message.timestamp]: updated
                                  }))}
                                  disabled={isGenerating}
                                />
                                <div className="ev-alert-card-warning mt-3 mb-2">
                                  <i className="bi bi-info-circle me-1"></i>
                                  I've extracted <b>{(editedSheets[message.timestamp] || message.inputSheetReview)?.input_parameters?.length || 0} parameters</b> from your documents. Click any cell to edit individual parameters and values, or use <b>Bulk Update via AI</b> for cross-cutting changes.
                                </div>
                                {message.references && (
                                  <ReferencesComponent references={message.references} />
                                )}
                                <div className="input-sheet-actions">
                                  <div className="ev-bold-med ev-small-p-blue mb-2">
                                    <i className="bi bi-asterisk gradient-font5 me-1"></i> What would you like to do next?
                                  </div>
                                  <div className="input-sheet-actions-row">
                                    <button
                                      className="ev-btn-gradient-fill-round ev-bold-reg"
                                      disabled={isGenerating}
                                      onClick={() => {
                                        const sheet = editedSheets[message.timestamp] || message.inputSheetReview;
                                        handleSendText("Proceed with generation", sheet);
                                      }}
                                    >
                                      <i className="bi bi-play-circle"></i> Generate Model
                                    </button>
                                    <button
                                      className="ev-btn-lightblue-fill-round avatar-tooltip ev-bold-reg"
                                
                                      disabled={isGenerating}
                                      onClick={() => {
                                        handleSendText("Fetch the model setup image or model settings diagram from the documents. Show the model structure, health state transition diagram, or model framework image.");
                                      }}
                                    >
                                      <i className="bi bi-image"></i> Generate Model Diagram
                                    </button>
                                    <button
                                      className="ev-btn-lightblue-fill-round avatar-tooltip ev-bold-reg"
                                      disabled={isGenerating}
                                      onClick={() => setSheetNlpVisible(prev => ({
                                        ...prev,
                                        [message.timestamp]: !prev[message.timestamp]
                                      }))}
                                    >
                                      <i className="bi bi-chat-text"></i> Update via AI
                                    </button>
                                  </div>

                                  {sheetNlpVisible[message.timestamp] && (
                                    <div className="input-sheet-refine-row">
                                      <input
                                        className="input-sheet-refine-input"
                                        type="text"
                                        value={sheetNlpInputs[message.timestamp] || ''}
                                        onChange={(e) => setSheetNlpInputs(prev => ({
                                          ...prev,
                                          [message.timestamp]: e.target.value
                                        }))}
                                        onKeyPress={(e) => {
                                          if (e.key === 'Enter' && sheetNlpInputs[message.timestamp]?.trim()) {
                                            const txt = sheetNlpInputs[message.timestamp].trim();
                                            setSheetNlpInputs(prev => ({ ...prev, [message.timestamp]: '' }));
                                            setSheetNlpVisible(prev => ({ ...prev, [message.timestamp]: false }));
                                            handleSendText(txt);
                                          }
                                        }}
                                        placeholder='e.g. "Convert all costs to EUR" or "Change all discount rates to 5%"'
                                        disabled={isGenerating}
                                      />
                                      <button
                                        className="input-sheet-refine-send"
                                        disabled={isGenerating || !sheetNlpInputs[message.timestamp]?.trim()}
                                        onClick={() => {
                                          if (sheetNlpInputs[message.timestamp]?.trim()) {
                                            const txt = sheetNlpInputs[message.timestamp].trim();
                                            setSheetNlpInputs(prev => ({ ...prev, [message.timestamp]: '' }));
                                            setSheetNlpVisible(prev => ({ ...prev, [message.timestamp]: false }));
                                            handleSendText(txt);
                                          }
                                        }}
                                      >
                                        <i className="bi bi-send-fill"></i>
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        )
                      )}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            <div ref={messagesEndRef} />

            {/* Enhanced Chat Input with Graph Selection */}
            <div className="chatInputFix navbar navbar-expand-lg fixed-bottom">

              {/* Input Section */}
              <div className="question-input">
                <input
                  className="chatSearch"
                  type="text"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask me anything"
                  disabled={isGenerating || selectedDocIds.length === 0}
                />
                {/* Graph Selection Panel */}
                <div className="d-flex">
                  <div className="chat-graph-panel">
                    {graphTypes.map((graph) => (
                      <button
                        key={graph.id}
                        className={`graph-btn ${selectedGraphTypes.includes(graph.id) ? 'active' : ''} avatar-tooltip`}
                        onClick={() => toggleGraphType(graph.id)}
                        data-tooltip={graph.label}
                      >
                        <i className={graph.icon}></i>
                      </button>
                    ))}
                  </div>
                  <button
                    className="chatSearchBtn"
                    onClick={handleAskQuestion}
                    disabled={!question || isGenerating || selectedDocIds.length === 0}
                  >
                    <i className="bi bi-cursor-fill"></i>
                  </button>
                </div>

              </div>
            </div>

          </main>
        </div>
      </div>

      {isErrorPopupVisible && (
        <ErrorPopup onClose={closeErrorPopup} message={popupErrorMessage} />
      )}

      {isSuccessPopupVisible && (
        <SuccessPopup onClose={() => setIsSuccessPopupVisible(false)} message={popupSuccessMessage} />
      )}

      {isProtocolLoading && (
        <ProcessingPopup message="Loading protocol chunks..." />
      )}

      {isModelsRefreshing && (
        <ProcessingPopup message="Refreshing generated models..." />
      )}

      {isProtocolSaving && (
        <ProcessingPopup message="Saving protocol..." />
      )}

      {/* Excel Model Preview Modal */}
      <DocumentPreview
        isOpen={excelPreviewOpen}
        onClose={() => { setExcelPreviewOpen(false); setExcelPreviewS3Path(null); setExcelPreviewName(null); setExcelPreviewUrl(null); }}
        presigned_url={excelPreviewUrl}
        documentName={excelPreviewName}
        showOpenInTab={true}
      />

      {/* Protocol Editor Right Sidebar — reuses ProtocolEditorSidebar from genbook */}
      {/* <ProtocolEditorSidebar
        isOpen={isProtocolEditorOpen && !!protocolDocId}
        onClose={() => setIsProtocolEditorOpen(false)}
        onSave={(updatedMarkdown) => {
          dispatch(setProtocolMarkdownContent(updatedMarkdown));
          handleSaveProtocol(updatedMarkdown);
        }}
        initialMarkdown={protocolMarkdownContent || ''}
        isLoading={isProtocolLoading}
        isSaving={isProtocolSaving}
      /> */}

      {/* Protocol Editor — empty state when no protocol selected */}
      {isProtocolEditorOpen && !protocolDocId && (
        <div className="protocol-editor-overlay">
          <div className="protocol-editor-sidebar">
            <div className="protocol-editor-header">
              <h3 className="protocol-editor-title">
                <i className="bi bi-pencil"></i> Model Protocol Editor
              </h3>
              <button
                className="protocol-editor-close"
                onClick={() => setIsProtocolEditorOpen(false)}
              >
                <i className="bi bi-x"></i>
              </button>
            </div>
            <div className="protocol-editor-content">
              <div className="text-center mt-5 p-4">
                <i className="bi bi-bookmark ev-xlarge-p-blue mb-3" style={{ fontSize: '2rem' }}></i>
                <p className="ev-reg-p-dark ev-bold-med">No Protocol Document Selected</p>
                <p className="ev-small-p-blue">
                  Select a document from the project tree and click the <b>Protocol</b> button to mark it as the protocol document for this chat.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Generated Models Slide-over (GenBI Manage Downloads pattern) */}
      {isGeneratedModelsOpen && (
        <div className="protocol-editor-overlay">
          <div className="protocol-editor-sidebar">
            {/* Header */}
            <div className="protocol-editor-header">
              <div className="d-flex align-items-center gap-2">
                <h5 className="ev-med-p-blue mb-0">
                  Generated Models&nbsp;
                  <span className="ev-reg">({generatedFiles.length})</span>
                </h5>
              </div>
              <button
                className="protocol-editor-close"
                onClick={() => { setIsGeneratedModelsOpen(false); setSelectedModelIds([]); }}
              >
                <i className="bi bi-x"></i>
              </button>
            </div>

            {/* Action Bar */}
            <div className="d-flex mt-3 mb-0 px-3 align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-2 ev-card-2 p-1 ev-radius-xsmall">
                <button
                  className="ev-btn-red-fill-round ev-btn-small ev-bold-reg"
                  onClick={() => {
                    setGeneratedFiles(prev => prev.filter(f => !selectedModelIds.includes(f.id)));
                    setSelectedModelIds([]);
                  }}
                  disabled={selectedModelIds.length === 0}
                >
                  <i className="bi bi-trash"></i> Delete Selected ({selectedModelIds.length})
                </button>
              </div>
              <button
                className="ev-btn-lightblue-fill-round ev-bold-reg"
                disabled={isModelsRefreshing}
                onClick={async () => {
                  if (sessionId) {
                    setIsModelsRefreshing(true);
                    try {
                      const resp = await axios.post('/pceapi/copilot/session-models', {
                        session_id: sessionId
                      }, {
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `${GetCookie('access_token')}`,
                        }
                      });
                      if (resp.data.status === 'success' && resp.data.data?.length > 0) {
                        setGeneratedFiles(resp.data.data.map(m => ({
                          id: m.id,
                          filename: m.model_name,
                          s3_path: m.s3_path,
                          message_id: m.message_id,
                          generated_at: m.generated_at,
                          status: 'ready',
                        })));
                        setSelectedModelIds([]);
                      }
                    } catch (err) {
                      console.error('Failed to fetch session models:', err);
                    } finally {
                      setIsModelsRefreshing(false);
                    }
                  }
                }}
              >
                <i className="bi bi-arrow-repeat"></i> Refresh
              </button>
            </div>

            {/* Table */}
            <div className="px-3 pb-2 mt-2" style={{ flex: 1, minHeight: 0 }}>
              <div className="table-container">
                <table className="table-custom1 mt-0 overflow-hidden w-100">
                  <thead>
                    <tr>
                      <th className="w-3">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={generatedFiles.length > 0 && selectedModelIds.length === generatedFiles.length}
                          ref={(cb) => { if (cb) cb.indeterminate = selectedModelIds.length > 0 && selectedModelIds.length < generatedFiles.length; }}
                          onChange={(e) => {
                            if (e.target.checked) setSelectedModelIds(generatedFiles.map(f => f.id));
                            else setSelectedModelIds([]);
                          }}
                        />
                      </th>
                      <th className="w-35"><label className="ev-small-p-blue">File Name</label></th>
                      <th className="w-25"><label className="ev-small-p-blue">Generated At</label></th>
                      <th className="w-30"><label className="ev-small-p-blue">Actions</label></th>
                    </tr>
                  </thead>
                </table>
                <div className="scrollable-element" style={{ maxHeight: "calc(100vh - 220px)" }}>
                  <table className="table-custom1 overflow-hidden w-100">
                    <tbody>
                      {generatedFiles.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="text-center">
                            <p className="ev-small-p-blue mt-5">
                              No models generated yet. Use the Excel Toolkit to generate models.
                            </p>
                          </td>
                        </tr>
                      ) : (
                        generatedFiles.map((file) => {
                          const displayName = file.filename || file.model_name;
                          const displayTime = file.generated_at
                            ? new Date(file.generated_at).toLocaleString()
                            : "N/A";
                          return (
                            <tr key={file.id}>
                              <td className="align-top w-3">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={selectedModelIds.includes(file.id)}
                                  onChange={() => {
                                    setSelectedModelIds(prev =>
                                      prev.includes(file.id) ? prev.filter(id => id !== file.id) : [...prev, file.id]
                                    );
                                  }}
                                />
                              </td>
                              <td className="align-top w-35 avatar-tooltip-mid" data-tooltip={displayName}>
                                <div className="d-flex align-items-center gap-2">
                                  <i className="bi bi-file-earmark-excel ev-small-p-blue flex-shrink-0"></i>
                                  <span className="ev-bold-med ev-small-p-dark text-truncate">
                                    {displayName.length > 20 ? displayName.substring(0, 20) + '...' : displayName}
                                  </span>
                                  <button
                                    className="ev-btn-blue-fill-round ev-btn-xsmall flex-shrink-0"
                                    onClick={() => handleViewExcelModel(file.s3_path, displayName)}
                                  >
                                    <i className="bi bi-eye"></i> View
                                  </button>
                                </div>
                              </td>
                              <td className="align-top w-25">
                                <span className="ev-reg-p-blue ev-fs-12">{displayTime}</span>
                              </td>
                              <td className="w-30">
                                <button
                                  className="ev-btn-blue-outline-round ev-btn-small ev-bold-reg me-2"
                                  onClick={() => handleDownloadExcelModel(file.s3_path, displayName)}
                                >
                                  Download <i className="bi bi-download"></i>
                                </button>
                                <button
                                  className="ev-btn-red-fill-round ev-btn-small ev-radius-xsmall pe-2 ps-2 avatar-tooltip"
                                  data-tooltip="Delete Model"
                                  onClick={() => {
                                    setGeneratedFiles(prev => prev.filter(f => f.id !== file.id));
                                    setSelectedModelIds(prev => prev.filter(id => id !== file.id));
                                  }}
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="d-flex justify-content-between align-items-center px-3 pb-3 mt-2">
              <div className="ev-small-p-dark">
                Showing {generatedFiles.length} of {generatedFiles.length} entries
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step Detail Popup — RAG Search Results (GenBI ViewLogs pattern) */}
      {activeStepDetail && activeStepDetail.detail && !activeStepDetail.details?.length && (
        <div
          className="ev-modal-overlay d-flex align-items-center justify-content-center p-3"
          onClick={(e) => { if (e.target === e.currentTarget) setActiveStepDetail(null); }}
        >
          <div className="ev-card-4 d-flex flex-column overflow-hidden vh-80 w-60">
            <div className="d-flex align-items-center justify-content-between ev-padding-small border-bottom flex-shrink-0">
              <div>
                <div className="d-flex align-items-center gap-2 mb-1">
                  <i className="bi bi-journal-text ev-small-p-blue"></i>
                  <span className="ev-bold-med ev-reg-p-dark">Search Results</span>
                </div>
                <span className="ev-message-card1 ev-btn-xsmall">{activeStepDetail.message}</span>
              </div>
              <button
                type="button"
                className="ev-btn-light-fill ev-btn-xsmall ev-radius-xlarge"
                onClick={() => setActiveStepDetail(null)}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <div className="ev-padding-small scrollable-element flex-grow-1 pt-2">
              {typeof activeStepDetail.detail === 'string' ? (
                <pre className="ev-card-2 ev-small-p-dark ev-radius-xsmall scrollable-element p-3" style={{ whiteSpace: 'pre-wrap' }}>
                  {activeStepDetail.detail}
                </pre>
              ) : activeStepDetail.detail && typeof activeStepDetail.detail === 'object' ? (
                <pre className="ev-card-2 ev-small-p-dark ev-radius-xsmall scrollable-element p-3" style={{ whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(activeStepDetail.detail, null, 2)}
                </pre>
              ) : (
                <div className="ev-card-2 text-center ev-padding-reg ev-radius-small">
                  <i className="bi bi-info-circle ev-fs-25 ev-small-p-blue d-block mb-2"></i>
                  <p className="ev-small-p-dark mb-0">No additional details available for this step.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Migrate Excel to Data Collection Modal */}
      <CopilotMigrateModal
        isOpen={migrateModalOpen}
        onClose={() => setMigrateModalOpen(false)}
        onSuccess={(msg) => { setPopupSuccessMessage(msg); setIsSuccessPopupVisible(true); }}
        onError={(msg) => { setPopupErrorMessage(msg); setIsErrorPopupVisible(true); }}
        fabricId={selectedFabricId}
        fileS3Path={migrateFileS3Path}
        fileName={migrateFileName}
      />

    </div>
  );
};

export default Copilot;